/**
 * Idempotent loader for the Google Maps JavaScript API + Places library.
 *
 * Why a loader and not a React wrapper library?
 *   • Zero new dependencies — uses the existing Maps key already in .env.
 *   • The Maps script is global and shared across all autocomplete fields,
 *     so multiple components mounting in parallel must coalesce onto a
 *     single load promise (Google's loader rejects double-load).
 *
 * Usage:
 *   const google = await loadGoogleMapsPlaces();
 *   new google.maps.places.Autocomplete(inputEl, { types: ['address'] });
 *
 * Radix Dialog/Sheet interaction:
 *   We do NOT intercept events at the document level here, because doing so
 *   reliably also blocks Google's own pac-item selection handlers. The
 *   correct fix is per-Dialog: pass an `onInteractOutside` handler that
 *   ignores clicks inside `.pac-container`. Use the helper
 *   `isInsidePacContainer(event)` exported below.
 */

const SCRIPT_ID = 'kro-google-maps-places-script';
let _loadPromise = null;

/**
 * Returns true iff the given event's target (or one of its ancestors) lives
 * inside Google's `.pac-container` autocomplete dropdown. Use this in any
 * Radix Dialog/Sheet's `onInteractOutside`/`onPointerDownOutside` to keep
 * the modal open when the user clicks a Places suggestion.
 *
 * Radix passes either a real event or an event-like object with `.detail`
 * or `.target`; we tolerate both shapes.
 */
export function isInsidePacContainer(event) {
  const target =
    event?.target ??
    event?.detail?.originalEvent?.target ??
    event?.detail?.target ??
    null;
  if (!target || typeof target.closest !== 'function') return false;
  return !!target.closest('.pac-container');
}

/**
 * Loads the Google Maps JS API (Places library) exactly once per page.
 * Resolves with the global `google` object. Rejects if the key is missing
 * or Google fails to load (offline, blocked, billing disabled).
 */
export function loadGoogleMapsPlaces() {
  if (_loadPromise) return _loadPromise;

  _loadPromise = new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      resolve(window.google);
      return;
    }

    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
      reject(new Error('REACT_APP_GOOGLE_MAPS_API_KEY is not configured'));
      return;
    }

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load',  () => resolve(window.google));
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load')));
      return;
    }

    const script = document.createElement('script');
    script.id    = SCRIPT_ID;
    script.async = true;
    script.defer = true;
    // v=3.64 pin — see public/index.html for full rationale. Must match the
    // root <script> tag so we never end up with two different Maps versions
    // on the same page if this loader is the first to fire.
    script.src   = `https://maps.googleapis.com/maps/api/js?v=3.64&key=${encodeURIComponent(key)}&libraries=places&loading=async`;
    script.onload  = () => {
      if (window.google?.maps?.places) {
        resolve(window.google);
      } else {
        reject(new Error('Google Maps loaded but Places library missing'));
      }
    };
    script.onerror = () => reject(new Error('Google Maps failed to load'));
    document.head.appendChild(script);
  });

  return _loadPromise;
}

/**
 * Reset the cached load promise — only useful for tests / forced retry.
 */
export function _resetGoogleMapsLoader() {
  _loadPromise = null;
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) existing.remove();
}
